import { EMPTY_STR } from "@/constants/string";
import { SORTING } from "@/constants/sorting";
import { useSorting } from "@/hooks/useSorting";
import { useMemo } from "react";

const SORT_KEYS = ["productName", "lastLoginAt", "startedAt"] as const;

export type TProductAccessSortKey = (typeof SORT_KEYS)[number];

type TSortableProductAccessRow = Record<TProductAccessSortKey, string>;

const EMPTY_SORT_VALUES = new Set<string>(["", EMPTY_STR.DATE, "-"]);

const isSortKey = (value: string): value is TProductAccessSortKey => (SORT_KEYS as readonly string[]).includes(value);

export const useSortedProductAccess = <T extends TSortableProductAccessRow>(rows: T[]): T[] => {
  const { sorts } = useSorting();

  return useMemo(() => {
    const [sortKey, sortOrder] = (sorts[0] ?? "").split("|");
    if (!isSortKey(sortKey)) return rows;

    const dir = sortOrder === SORTING.DESC ? -1 : 1;

    return [...rows].sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      const aEmpty = EMPTY_SORT_VALUES.has(av);
      const bEmpty = EMPTY_SORT_VALUES.has(bv);
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      return av.localeCompare(bv) * dir;
    });
  }, [rows, sorts]);
};
